import { DICTIONARIES_IN_API, AD_PLACEMENTS } from "@consts";
import { hasDictionaryScoreField, sortObjectKeys } from "@utils";

export default {
	onGameSelectTriggerClick() {
		this.gameSelectIsOpen = !this.gameSelectIsOpen;
	},
	onGameChange(newDictionary) {
		this.$store.commit("query/setDictionary", newDictionary.value);
		// this.$store.commit("updatePrefixedTiles", "");

		const eventID = {
			[DICTIONARIES_IN_API.WWF]: 75,
			[DICTIONARIES_IN_API.US]: 76,
			[DICTIONARIES_IN_API.UK]: 77,
			[`${DICTIONARIES_IN_API.US}${DICTIONARIES_IN_API.UK}`]: 78,
			[DICTIONARIES_IN_API.CHU]: 80,
			[DICTIONARIES_IN_API.ALL]: 81,
		};

		this.$store.dispatch("tracking/trackEvent", {
			eventCategory: "Game selection",
			eventAction: "Selects dictionary from scroll-down menu",
			eventLabel: this.$t(this.dictionary.text),
			eventID: eventID[this.dictionary.value],
		});

		if (this.isSmallDevice) {
			this.closeMobileBoxControls();
		}

		/**
		 * @note
		 * this second GA event is what was originally in the
		 * AdvancedFilters component. While it's ridiculous to have
		 * both GA events next to each other, we're keeping it here
		 * for consistency while we decide what to do with the
		 * GA events. We're going to be migrating GA soon anyway.
		 *
		 * The GA events are duplicated both in the results-en-new
		 * page and in the Finder component (both places that render
		 * the GameSelect component) because it is unclear whether
		 * they will have the same GA event in the future.
		 */
		const eventCategory = this.isScrabbleResultsPage
			? "Scrabble Dictionary"
			: "Filter menus";
		const eventAction = this.isScrabbleResultsPage
			? `Select ${this.$t(this.dictionary.text)} dictionary`
			: `Click on ${this.$t(this.dictionary.text)} dictionary`;
		const eventLabel = this.isScrabbleResultsPage
			? `ScrabbleDictionaryResultsTextScrabble${this.dictionary.value}`
			: this.dictionary.text;
		const secondEventID = {
			[DICTIONARIES_IN_API.WWF]: 48,
			[DICTIONARIES_IN_API.US]: this.isScrabbleResultsPage ? 126 : 49,
			[DICTIONARIES_IN_API.UK]: this.isScrabbleResultsPage ? 127 : 50,
			[`${DICTIONARIES_IN_API.US}${DICTIONARIES_IN_API.UK}`]: 51,
			[DICTIONARIES_IN_API.CHU]: 52,
		};

		this.$store.dispatch("tracking/trackEvent", {
			eventID: secondEventID[this.$t(this.dictionary.value)],
			eventCategory,
			eventAction,
			eventLabel,
		});

		this.gameSelectIsOpen = false;

		let newSort;

		if (
			(this.$route.query?.order_by === "score" ||
				!this.$route.query?.order_by) &&
			!hasDictionaryScoreField(newDictionary.value)
		) {
			newSort = "alpha";
		} else if (
			!this.$route.query?.order_by &&
			hasDictionaryScoreField(newDictionary.value)
		) {
			newSort = "score";
		} else {
			newSort = this.$route.query?.order_by;
		}

		this.$router.push({
			query: {
				...this.$route.query,
				dictionary: newDictionary.value,
				order_by: newSort,
			},
		});

		this.currentSort = newSort;
	},
	onGameNotChange() {
		if (this.isSmallDevice) {
			this.closeMobileBoxControls();
		}
		this.gameSelectIsOpen = false;
	},
	onGameSelectClickOutside(e) {
		const trigger = this.$refs?.gameSelectTrigger?.$el;
		if (trigger && e.composedPath().includes(trigger)) {
			return;
		}

		this.gameSelectIsOpen = false;
	},
	onPersistentSearchClick() {
		if (this.isScrabbleDictionaryResultsPage) {
			this.showScrabbleFinder = true;
			return;
		}

		if (this.isAnagramResultsPage) {
			this.showAnagramFinder = true;
			return;
		}

		return this.openMobileBoxControls("search");
	},
	onScrabbleFinderClickOutside(e) {
		if (e?.composedPath?.()?.includes(this.$refs?.persistentSearch?.$el)) {
			return;
		}
		this.showAnagramFinder = false;
		this.showScrabbleFinder = false;
	},
	openMobileBoxControls(type) {
		this.mobileBoxControlsMode = type;
	},
	closeMobileBoxControls() {
		this.mobileBoxControlsMode = null;
	},
	closeMobileBoxControlsAndLoad() {
		this.loading = true;
		this.mobileBoxControlsMode = null;
	},
	onSortChange(sortMode) {
		this.$router.push({
			query: { ...this.$route.query, order_by: sortMode },
		});
		this.currentSort = sortMode;
	},
	sortedWords(useAlternatives = false) {
		const resultWords = useAlternatives
			? this.alternativeResults
			: this.results;
		return sortObjectKeys(resultWords).map((key) => resultWords[key]);
	},
	submitSearch() {
		this.closeMobileBoxControls();
		this.$fetch();
	},
	tablesWithAds(useAlternatives = false) {
		const output = [];
		let totalInContentAds =
			this.isSmallOrMediumPortraitDevice &&
			this.isScrabbleDictionaryResultsPage
				? 1
				: 0;
		let totalInListAds = 0;

		if (useAlternatives) {
			/**
			 * We need to check the output of this same method from the
			 * primary results, in order to know how many ads were loaded
			 * into the primary results. Otherwise we're going to have
			 * the same ad units repeated in the alternative results.
			 *
			 * I highly dislike the idea of running this method AGAIN,
			 * but I don't see another way around it without massively
			 * retooling how we calculate the ads for both primary and
			 * alternative results.
			 *
			 * Still, meh.
			 */
			const nonAlternativeTableData = this.tablesWithAds();

			totalInContentAds =
				nonAlternativeTableData?.[0]?.rowAds?.[0]?.totalInContentAds ||
				0;
			totalInListAds =
				nonAlternativeTableData?.[0]?.rowAds?.[0]?.totalInListAds || 0;
		}

		/**
		 * This value is used to affect the position of the ads.
		 * Mobile ads start after the 3rd row, while desktop ads
		 * start after the 9th row.
		 */
		const mobilePositionOffset = 3;
		const desktopPositionOffset = 9;
		const maxTableAds = 5;

		const adStub = this.isSmallOrMediumPortraitDevice
			? this.AD_LAYOUT_STUBS.MOB_IN_CONTENT
			: this.AD_LAYOUT_STUBS.DT_IN_CONTENT;

		this.sortedWords(useAlternatives).forEach((wordGroup, i) => {
			const data = {
				...wordGroup,
				/**
				 * Ads that appear between rows inside the table
				 */
				rowAds: [],
				/**
				 * Ads that appear below the table
				 */
				tableAds: [],
				// isAlternativesTable: wordGroup.alternativeWords,
				// isSkeletonTable:
				// 	wordsData[wordGroup.key]?._items[0]?.word.includes(
				// 		"skeleton-word"
				// 	),
			};

			if (i === 0) {
				/**
				 * For mobile, we want the first ad to go either after
				 * the 3rd row of the first table, or whatever the last
				 * row is if it's less than 3.
				 *
				 * For desktop, it should go after the 9th row. If the
				 * table does not have 9 rows, it does not appear.
				 */
				const rowsInFirstTable = data._items.length;
				const firstMobileAdPosition = Math.min(
					mobilePositionOffset,
					rowsInFirstTable
				);
				const firstDesktopAdPosition =
					rowsInFirstTable > desktopPositionOffset
						? desktopPositionOffset
						: null;

				data.rowAds.push({
					position: firstMobileAdPosition,
					stub: adStub,
					type: "mobile",
					placement:
						this.AD_PLACEMENTS[
							`MOB_IN_CONTENT_${totalInContentAds + 1}`
						],
				});

				if (firstDesktopAdPosition !== null) {
					data.rowAds.push({
						position: firstDesktopAdPosition,
						stub: adStub,
						type: "desktop",
						placement:
							this.AD_PLACEMENTS[
								`DT_IN_CONTENT_${totalInContentAds + 1}`
							],
					});
				}

				if (
					this.isSmallOrMediumPortraitDevice ||
					firstDesktopAdPosition !== null
				) {
					totalInContentAds++;
				}
			}

			if (i > 0 && i <= maxTableAds) {
				data.tableAds.push({
					type: "both",
					stub: adStub,
					placement:
						this.AD_PLACEMENTS[
							this.isSmallOrMediumPortraitDevice
								? `MOB_IN_CONTENT_${totalInContentAds + 1}`
								: `DT_IN_CONTENT_${totalInContentAds + 1}`
						],
				});
				totalInContentAds++;
			}

			const wordsTotal = data._meta?.total;
			let inListAdPosition = null;
			if (wordsTotal > this.wordsCount.second) {
				inListAdPosition = this.wordsCount.second - 2;
			} else if (wordsTotal > this.wordsCount.initial) {
				inListAdPosition = wordsTotal;
			}

			if (inListAdPosition !== null) {
				const proposedAdPlacement = this.isSmallOrMediumPortraitDevice
					? `MOB_IN_LIST_${totalInListAds + 1}`
					: `DT_IN_LIST_${totalInListAds + 1}`;

				if (this.AD_PLACEMENTS[proposedAdPlacement]) {
					data.rowAds.push({
						type: "both",
						// TODO add here a flag to say this is movbile or desktop only
						stub: adStub,
						placement: this.AD_PLACEMENTS[proposedAdPlacement],
						position: inListAdPosition,
						positionHTL: "in_content",
					});

					totalInListAds++;
				}
			}

			output[i] = data;
		});

		output.forEach((group) => {
			// Append the total in-content ad count to every ad entry.
			group.rowAds.forEach((ad) => {
				ad.totalInContentAds = totalInContentAds;
				ad.totalInListAds = totalInListAds;
				ad.showAd =
					(this.isSmallOrMediumPortraitDevice &&
						["mobile", "both"].includes(ad.type)) ||
					(this.isMediumLandscapeDeviceOrWider &&
						["desktop", "both"].includes(ad.type));
			});
			group.tableAds.forEach((ad) => {
				ad.totalInContentAds = totalInContentAds;
				ad.totalInListAds = totalInListAds;
				ad.showAd =
					(this.isSmallOrMediumPortraitDevice &&
						["mobile", "both"].includes(ad.type)) ||
					(this.isMediumLandscapeDeviceOrWider &&
						["desktop", "both"].includes(ad.type));
			});
		});
		const MULTIPLE_ADS_MIN_RESULTS = 15;
		if (
			this.totalResultsCount + this.totalAlternativeResultsCount <=
			MULTIPLE_ADS_MIN_RESULTS
		) {
			output.forEach((group) => {
				group.rowAds = group.rowAds.filter((ad) => {
					return (
						ad.placement === AD_PLACEMENTS.MOB_IN_CONTENT_1 ||
						ad.placement.includes("dt_")
					);
				});
				group.tableAds = group.tableAds.filter((ad) => {
					return (
						ad.placement === AD_PLACEMENTS.MOB_IN_CONTENT_1 ||
						ad.placement.includes("dt_")
					);
				});
			});
		}
		return output;
	},

	fortmattedResults(data) {
		Object.values(data).forEach((group) => {
			if (this.isWordleResultsPage) {
				group.tableHeaderText = `${this.$t(
					"ResultsWordsNumberTableTitle",
					{
						numberQuery: group.word_length,
					}
				)} for Wordle`;
			} else if (this.isQuordleResultsPage) {
				group.tableHeaderText = `${this.$t(
					"ResultsWordsNumberTableTitle",
					{
						numberQuery: group.word_length,
					}
				)} for Quordle`;
			} else {
				group.tableHeaderText = this.getWordListTableHeadingText(
					group.word_length
				);
			}
		});
		return data;
	},
};
